import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTint, 
  faArrowLeft, 
  faSave, 
  faTimes, 
  faMagic,
  faInfoCircle,
  faFlask,
  faTable,
  faUserCheck,
  faComment,
  faEye,
  faFilePdf,
  faEdit,
  faPencil
} from '@fortawesome/free-solid-svg-icons';
import { generateLiquidAdmixturePDF } from './LiquidAdmixturePDFGenerator';
import { getCustomers } from '../../../apis/customers';
import { getReviewers } from '../../../apis/reviewers';
import { createLiquidAdmixtureTest, updateLiquidAdmixtureTest, getLiquidAdmixtureTest } from '../../../apis/liquidAdmixture';
import { formatDate, yyyymmddToDDMMYYYY, ddmmyyyyToYYYYMMDD } from '../../../utils/dateFormatter';

const LiquidAdmixtureForm = () => {
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testId, setTestId] = useState(null);
  
  // Customer data state
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [customersError, setCustomersError] = useState(null);
  
  // Searchable dropdown state
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');
  
  // Reviewers data state
  const [reviewers, setReviewers] = useState([]);
  const [loadingReviewers, setLoadingReviewers] = useState(false);
  
  const [formData, setFormData] = useState({
    // General Information
    customer_id: '',
    url_number: '',
    job_code_number: '',
    reference_number: '',
    sample_description: '',
    date_of_receipt: '',
    sample_test_code: '',
    date_of_testing: new Date().toISOString().split('T')[0],
    environmental_conditions: 'Laboratory Conditions',
    site_name: '',
    site_address: '',
    
    // Test Results
    colour_texture_1: '', volume_ml_1: '', temperature_c_1: '', hydrometer_reading_1: '', relative_density_1: '',
    colour_texture_2: '', volume_ml_2: '', temperature_c_2: '', hydrometer_reading_2: '', relative_density_2: '',
    colour_texture_3: '', volume_ml_3: '', temperature_c_3: '', hydrometer_reading_3: '', relative_density_3: '',
    average_relative_density: '',
    
    // Verification
    tested_by_name: '',
    tested_by_date: new Date().toISOString().split('T')[0],
    checked_by_name: '',
    checked_by_date: new Date().toISOString().split('T')[0],
    verified_by_name: 'Prakarsh A Sangave',
    verified_by_date: new Date().toISOString().split('T')[0],
    reviewed_by: 'Lalita S. Dussa - Quality Manager',
    authorized_by: 'Prakarsh Sangave',
    remarks: ''
  });

  // Fetch customers on component mount
  useEffect(() => {
    const fetchCustomersData = async () => {
      try {
        setCustomersLoading(true);
        setCustomersError(null);
        const customersData = await getCustomers();
        setCustomers(customersData);
        setFilteredCustomers(customersData);
      } catch (error) {
        console.error('Error fetching customers:', error);
        setCustomersError('Failed to load customers');
      } finally {
        setCustomersLoading(false);
      }
    };

    fetchCustomersData();
  }, []);

  // Load test data if testId is in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const testIdFromUrl = urlParams.get('testId');
    const isEditMode = urlParams.get('edit') === 'true';
    
    if (testIdFromUrl) {
      console.log('🔵 [LIQUID ADMIXTURE FORM] Loading test from URL, ID:', testIdFromUrl);
      setTestId(parseInt(testIdFromUrl));
      
      const loadTestData = async () => {
        try {
          const testData = await getLiquidAdmixtureTest(parseInt(testIdFromUrl));
          console.log('✅ [LIQUID ADMIXTURE FORM] Test data loaded:', testData);
          
          // Update formData with loaded data
          if (testData.readings && testData.readings.length > 0) {
            testData.readings.forEach((reading, index) => {
              const i = index + 1;
              setFormData(prev => ({
                ...prev,
                [`colour_texture_${i}`]: reading.colour_texture || '',
                [`volume_ml_${i}`]: reading.volume_ml ? reading.volume_ml.toString() : '',
                [`temperature_c_${i}`]: reading.temperature_c ? reading.temperature_c.toString() : '',
                [`hydrometer_reading_${i}`]: reading.hydrometer_reading ? reading.hydrometer_reading.toString() : '',
                [`relative_density_${i}`]: reading.relative_density ? reading.relative_density.toString() : ''
              }));
            });
          }
          
          // Update other form fields
          // Convert dates from DD/MM/YYYY (backend) to YYYY-MM-DD (for date inputs)
          setFormData(prev => ({
            ...prev,
            customer_id: testData.customer_id ? testData.customer_id.toString() : '',
            url_number: testData.url_number || '',
            job_code_number: testData.job_code_number || '',
            reference_number: testData.reference_number || '',
            sample_description: testData.sample_description || '',
            date_of_receipt: testData.date_of_receipt ? ddmmyyyyToYYYYMMDD(testData.date_of_receipt) : '',
            sample_test_code: testData.sample_test_code || '',
            date_of_testing: testData.date_of_testing ? ddmmyyyyToYYYYMMDD(testData.date_of_testing) : new Date().toISOString().split('T')[0],
            environmental_conditions: testData.environmental_conditions || 'Laboratory Conditions',
            tested_by_name: testData.tested_by_name || '',
            tested_by_date: testData.tested_by_date ? ddmmyyyyToYYYYMMDD(testData.tested_by_date) : new Date().toISOString().split('T')[0],
            checked_by_name: testData.checked_by_name || '',
            checked_by_date: testData.checked_by_date ? ddmmyyyyToYYYYMMDD(testData.checked_by_date) : new Date().toISOString().split('T')[0],
            verified_by_name: testData.verified_by_name || 'Prakarsh A Sangave',
            verified_by_date: testData.verified_by_date ? ddmmyyyyToYYYYMMDD(testData.verified_by_date) : new Date().toISOString().split('T')[0],
            reviewed_by: testData.reviewed_by || 'Lalita S. Dussa - Quality Manager',
            authorized_by: testData.authorized_by || 'Prakarsh Sangave',
            remarks: testData.remarks || '',
            average_relative_density: testData.specific_gravity ? testData.specific_gravity.toFixed(4) : ''
          }));
          
          if (testData.customer_name) {
            setCustomerSearchTerm(testData.customer_name);
          }
          
          // If edit mode, show form; otherwise show preview
          if (isEditMode) {
            setShowPreview(false);
          } else {
            setShowPreview(true);
          }
        } catch (error) {
          console.error('❌ [LIQUID ADMIXTURE FORM] Error loading test:', error);
          alert('Failed to load test data');
        }
      };
      
      loadTestData();
    }
  }, []);

  // Fetch reviewers from database
  useEffect(() => {
    const fetchReviewers = async () => {
      setLoadingReviewers(true);
      try {
        const reviewersData = await getReviewers();
        setReviewers(reviewersData);
      } catch (error) {
        console.error('Error fetching reviewers:', error);
        // Fallback to hardcoded reviewers if API fails
        setReviewers([
          { id: 1, name: 'Lalita S. Dussa', designation: 'Quality Manager', graduation: 'B.Tech.(Civil)' },
          { id: 2, name: 'Harsha Prakarsha Sangave', designation: 'Quality Manager', graduation: 'M.E(Civil-Structures)' },
          { id: 3, name: 'Amol A Adam', designation: 'Quality Manager', graduation: 'B.E(Civil)' },
          { id: 4, name: 'Aaquib J. Shaikh', designation: 'Quality Manager', graduation: 'B.E(Civil)' },
          { id: 5, name: 'Prakarsha A. Sangave', designation: 'Chief Executive Officer', graduation: 'M.E(Civil-Structures)' }
        ]);
      } finally {
        setLoadingReviewers(false);
      }
    };

    fetchReviewers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter customers based on dropdown search term
  useEffect(() => {
    if (dropdownSearchTerm.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        customer.name?.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
        (customer.phone && customer.phone.includes(dropdownSearchTerm)) ||
        (customer.city && customer.city.toLowerCase().includes(dropdownSearchTerm.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    }
  }, [dropdownSearchTerm, customers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown]);

  // Calculate average on component mount
  useEffect(() => {
    // Delay calculation to ensure formData is properly initialized
    setTimeout(() => {
      calculateAverage();
    }, 100);
  }, []);

  // Add Cube Testing Style CSS to match reference template
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Cube Testing Style for Form Controls */
      .form-control, .form-select {
        background-color: #1C2333 !important;
        color: white !important;
        border: 2px solid #6c757d !important;
        border-radius: 8px !important;
        padding: 12px 15px !important;
      }
      
      .form-control:focus, .form-select:focus {
        background-color: #1C2333 !important;
        color: white !important;
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
        outline: none !important;
      }
      
      .form-control::placeholder {
        color: #adb5bd !important;
        opacity: 0.8 !important;
      }
      
      .form-select option {
        background-color: #1C2333 !important;
        color: white !important;
      }
      
      .card {
        background-color: #1C2333 !important;
        border: 2px solid #6c757d !important;
        border-radius: 12px !important;
      }
      
      .card-header {
        background-color: #6c757d !important;
        color: white !important;
        border-bottom: 2px solid #6c757d !important;
        border-radius: 10px 10px 0 0 !important;
        padding: 15px 20px !important;
      }
      
      .card-body {
        background-color: #1C2333 !important;
        color: white !important;
        padding: 20px !important;
      }
      
      .form-label {
        color: #e2e8f0 !important;
        font-weight: 600 !important;
        font-size: 0.95rem !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
        margin-bottom: 8px !important;
      }
      
      .form-label.required-field::after {
        content: " *";
        color: #FFA500 !important;
      }
      
      /* Table styling */
      .table {
        background-color: #1C2333 !important;
        color: white !important;
      }
      
      .table thead th {
        background-color: #6c757d !important;
        color: white !important;
        border: 1px solid #6c757d !important;
        font-weight: 700 !important;
      }
      
      .table tbody td {
        background-color: #1C2333 !important;
        color: white !important;
        border: 1px solid #6c757d !important;
      }
      
      /* Remove all hover effects */
      .table tbody tr:hover,
      .table tbody tr:hover td,
      .transparent-table tbody tr:hover,
      .transparent-table tbody tr:hover td,
      .transparent-table tbody tr.table-info:hover,
      .transparent-table tbody tr.table-info:hover td {
        background-color: inherit !important;
        transform: none !important;
        box-shadow: none !important;
      }
      
      .table tbody tr.table-info {
        background-color: #495057 !important;
      }
      
      .table tbody tr.table-info td {
        background-color: #495057 !important;
        color: white !important;
        font-weight: 700 !important;
      }
      
      /* Input fields inside table */
      .table .form-control {
        background-color: #242B3C !important;
        color: white !important;
        border: 1px solid #6c757d !important;
      }
      
      .table .form-control:focus {
        background-color: #242B3C !important;
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.2rem rgba(255, 165, 0, 0.25) !important;
      }
      
      .table .form-control[readonly] {
        background-color: #495057 !important;
        color: #ffffff !important;
        cursor: not-allowed !important;
      }
      
      /* Textarea styling */
      textarea.form-control {
        min-height: 100px !important;
        resize: vertical !important;
      }
      
      /* Date input styling */
      input[type="date"] {
        color-scheme: dark !important;
      }
      
      input[type="date"]::-webkit-calendar-picker-indicator {
        filter: invert(1) !important;
        cursor: pointer !important;
      }
      
      /* H6 headings */
      h6 {
        color: #FFA500 !important;
        font-weight: 600 !important;
        margin-bottom: 15px !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
      }
      
      /* Button styling to match cube testing - Remove all hover effects */
      .btn-primary,
      .btn-primary:hover,
      .btn-primary:focus,
      .btn-primary:active {
        background-color: #007bff !important;
        border-color: #007bff !important;
        color: white !important;
        font-weight: 600 !important;
        padding: 12px 24px !important;
        border-radius: 8px !important;
        transform: none !important;
        box-shadow: none !important;
      }
      
      .btn-secondary,
      .btn-secondary:hover,
      .btn-secondary:focus,
      .btn-secondary:active {
        background-color: #6c757d !important;
        border-color: #6c757d !important;
        color: white !important;
        font-weight: 600 !important;
        padding: 12px 24px !important;
        border-radius: 8px !important;
        transform: none !important;
        box-shadow: none !important;
      }
      
      /* Disable all hover effects globally for this form */
      .card:hover,
      .card-header:hover,
      .card-body:hover {
        transform: none !important;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3) !important;
      }
      
      /* Icon styling */
      .fas, .fa {
        margin-right: 8px;
      }
      
      /* Card shadow enhancement - No hover effects */
      .card {
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3) !important;
      }
      
      .card:hover {
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3) !important;
        transform: none !important;
      }
      
      /* Disable all hover effects for form elements */
      .form-control:hover,
      .form-select:hover,
      .form-control:active,
      .form-select:active {
        transform: none !important;
        box-shadow: none !important;
        border-color: #6c757d !important;
      }
      
      /* Only allow focus states, no hover */
      .form-control:focus,
      .form-select:focus {
        border-color: #FFA500 !important;
        box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Handle customer selection from dropdown
  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id.toString(),
      site_name: customer.site_name || '',
      site_address: customer.address || ''
    }));
    setCustomerSearchTerm(customer.name);
    setShowCustomerDropdown(false);
    setDropdownSearchTerm(''); // Clear dropdown search
  };

  // Handle dropdown search input
  const handleDropdownSearch = (e) => {
    setDropdownSearchTerm(e.target.value);
  };

  // Handle customer input click to show dropdown
  const handleCustomerInputClick = () => {
    setShowCustomerDropdown(true);
    setDropdownSearchTerm(''); // Clear search when opening dropdown
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const updatedFormData = {
        ...prev,
        [name]: value
      };
      
      // Auto-calculate average when relative density values change
      if (name.startsWith('relative_density_')) {
        setTimeout(() => {
          calculateAverage(updatedFormData);
        }, 0);
      }
      
      return updatedFormData;
    });
  };

  const calculateAverage = (currentFormData = formData) => {
    let sum = 0;
    let count = 0;
    
    console.log('Calculating average with data:', currentFormData);
    
    for (let row = 1; row <= 3; row++) {
      const fieldName = `relative_density_${row}`;
      const relativeDensity = parseFloat(currentFormData[fieldName]) || 0;
      console.log(`Row ${row}: ${fieldName} = ${currentFormData[fieldName]} -> parsed: ${relativeDensity}`);
      
      if (relativeDensity > 0) {
        sum += relativeDensity;
        count++;
      }
    }
    
    const average = count > 0 ? sum / count : 0;
    console.log(`Sum: ${sum}, Count: ${count}, Average: ${average}`);
    
    setFormData(prev => ({
      ...prev,
      average_relative_density: average > 0 ? average.toFixed(4) : ''
    }));
  };

  // Test function for debugging - can be called from browser console
  window.testCalculation = () => {
    console.log('Current formData:', formData);
    calculateAverage();
  };

  // Preview and PDF functions
  const handleSaveTestData = async () => {
    // Validate required fields
    if (!formData.customer_id) {
      alert('Please select a customer');
      return;
    }
    
    setIsSaving(true);
    console.log('🔵 [LIQUID ADMIXTURE FORM] Saving test data...');
    console.log('📊 [LIQUID ADMIXTURE FORM] Form data:', formData);
    
    try {
      // Prepare readings data
      const readings = [];
      for (let i = 1; i <= 3; i++) {
        if (formData[`relative_density_${i}`] || formData[`hydrometer_reading_${i}`]) {
          readings.push({
            sr_no: i,
            colour_texture: formData[`colour_texture_${i}`] || '',
            volume_ml: formData[`volume_ml_${i}`] ? parseFloat(formData[`volume_ml_${i}`]) : null,
            temperature_c: formData[`temperature_c_${i}`] ? parseFloat(formData[`temperature_c_${i}`]) : null,
            hydrometer_reading: formData[`hydrometer_reading_${i}`] ? parseFloat(formData[`hydrometer_reading_${i}`]) : null,
            relative_density: formData[`relative_density_${i}`] ? parseFloat(formData[`relative_density_${i}`]) : null
          });
        }
      }
      
      // Calculate average relative density
      const densities = readings
        .map(r => r.relative_density)
        .filter(d => d !== null && d !== undefined && !isNaN(d));
      const average = densities.length > 0 
        ? densities.reduce((sum, d) => sum + d, 0) / densities.length 
        : 0;
      
      // Prepare test data for API
      const testData = {
        customer_id: parseInt(formData.customer_id),
        url_number: formData.url_number || '',
        job_code_number: formData.job_code_number || '',
        reference_number: formData.reference_number || '',
        sample_description: formData.sample_description || '',
        date_of_receipt: formData.date_of_receipt || null,
        sample_test_code: formData.sample_test_code || '',
        date_of_testing: formData.date_of_testing || null,
        environmental_conditions: formData.environmental_conditions || 'Laboratory Conditions',
        tested_by_name: formData.tested_by_name || '',
        tested_by_date: formData.tested_by_date || null,
        checked_by_name: formData.checked_by_name || '',
        checked_by_date: formData.checked_by_date || null,
        verified_by_name: formData.verified_by_name || 'Prakarsh A Sangave',
        verified_by_date: formData.verified_by_date || null,
        reviewed_by: formData.reviewed_by || '',
        authorized_by: formData.authorized_by || 'Prakarsh Sangave',
        remarks: formData.remarks || '',
        specific_gravity: average > 0 ? average : null,
        readings: readings
      };
      
      console.log('📤 [LIQUID ADMIXTURE FORM] Sending test data to API:', testData);
      
      let savedTest;
      if (testId) {
        // Update existing test
        console.log('🔄 [LIQUID ADMIXTURE FORM] Updating test ID:', testId);
        savedTest = await updateLiquidAdmixtureTest(testId, testData);
        console.log('✅ [LIQUID ADMIXTURE FORM] Test updated successfully:', savedTest);
      } else {
        // Create new test
        console.log('➕ [LIQUID ADMIXTURE FORM] Creating new test');
        savedTest = await createLiquidAdmixtureTest(testData);
        console.log('✅ [LIQUID ADMIXTURE FORM] Test created successfully:', savedTest);
        setTestId(savedTest.test_id || savedTest.id);
      }
      
      // Show preview after successful save
      setShowPreview(true);
      console.log('✅ [LIQUID ADMIXTURE FORM] Data saved successfully, showing preview');
      
    } catch (error) {
      console.error('❌ [LIQUID ADMIXTURE FORM] Error saving test data:', error);
      // Error alert is already shown by handleApiError in the API file
      // Only show additional alert if error message is not user-friendly
      if (!error.message || error.message.includes('API endpoint not found')) {
        // Error handler already showed the alert, no need for duplicate
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for Observation Sheet
  const handleViewObservationSheet = () => {
    const params = new URLSearchParams();
    
    // General Information (for Observation Sheet) - Format dates to DD/MM/YYYY (only if date exists)
    params.append('sample_description', formData.sample_description || '');
    params.append('date_of_receipt', formData.date_of_receipt ? formatDate(formData.date_of_receipt) : '');
    params.append('sample_test_code', formData.sample_test_code || '');
    params.append('date_of_testing', formData.date_of_testing ? formatDate(formData.date_of_testing) : '');
    params.append('environmental_conditions', formData.environmental_conditions || '');
    
    // Test Results (for Observation Sheet) - Format relative density to 2 decimals
    for (let i = 1; i <= 3; i++) {
      params.append(`colour_texture_${i}`, formData[`colour_texture_${i}`] || '');
      params.append(`volume_ml_${i}`, formData[`volume_ml_${i}`] || '');
      params.append(`temperature_c_${i}`, formData[`temperature_c_${i}`] || '');
      params.append(`hydrometer_reading_${i}`, formData[`hydrometer_reading_${i}`] || '');
      // Format relative density to 2 decimal places
      const density = formData[`relative_density_${i}`];
      params.append(`relative_density_${i}`, density ? parseFloat(density).toFixed(2) : '');
    }
    
    // Verification (for Observation Sheet) - Format dates to DD/MM/YYYY (only if date exists)
    params.append('tested_by_name', formData.tested_by_name || '');
    params.append('tested_by_date', formData.tested_by_date ? formatDate(formData.tested_by_date) : '');
    params.append('checked_by_name', formData.checked_by_name || '');
    params.append('checked_by_date', formData.checked_by_date ? formatDate(formData.checked_by_date) : '');
    params.append('verified_by_name', formData.verified_by_name || '');
    params.append('verified_by_date', formData.verified_by_date ? formatDate(formData.verified_by_date) : '');
    
    // Remarks (for Observation Sheet)
    params.append('remarks', formData.remarks || '');
    
    // Open Observation Sheet
    const observationSheetUrl = `/LiquidAdmixture/LiquidAdmixtureObservationSheet.html?${params.toString()}`;
    window.open(observationSheetUrl, '_blank');
  };

  // Handler for Test Report
  const handleViewReport = () => {
    const params = new URLSearchParams();
    
    // General Information (for Observation Sheet - needed for average calculation) - Format dates to DD/MM/YYYY (only if date exists)
    params.append('sample_description', formData.sample_description || '');
    params.append('date_of_receipt', formData.date_of_receipt ? formatDate(formData.date_of_receipt) : '');
    params.append('sample_test_code', formData.sample_test_code || '');
    params.append('date_of_testing', formData.date_of_testing ? formatDate(formData.date_of_testing) : '');
    params.append('environmental_conditions', formData.environmental_conditions || '');
    
    // Test Results (for average calculation) - Format to 2 decimals
    for (let i = 1; i <= 3; i++) {
      const density = formData[`relative_density_${i}`];
      params.append(`relative_density_${i}`, density ? parseFloat(density).toFixed(2) : '');
    }
    
    // Customer Information (for Test Report)
    params.append('customer_name', customerSearchTerm || formData.customer_id || '');
    params.append('site_name', formData.site_name || '');
    params.append('site_address', formData.site_address || '');
    params.append('ulr_number', formData.url_number || '');
    params.append('url_number', formData.url_number || '');
    params.append('job_code_number', formData.job_code_number || '');
    params.append('reference_number', formData.reference_number || '');
    params.append('date_of_report', formatDate(new Date()));
    params.append('date_of_material_receipt', formData.date_of_receipt ? formatDate(formData.date_of_receipt) : '');
    params.append('material_description', formData.sample_description || '');
    params.append('condition_of_sample', 'Acceptable');
    params.append('location_of_testing', 'Lab');
    
    // Test Results Summary (for Test Report) - Format to 2 decimals
    const avgDensity = formData.average_relative_density ? parseFloat(formData.average_relative_density).toFixed(2) : '0.00';
    params.append('specific_gravity_result', avgDensity);
    params.append('average_relative_density', avgDensity);
    
    // Authorization (for Test Report)
    params.append('reviewed_by', formData.reviewed_by || 'Lalita S. Dussa - Quality Manager');
    params.append('authorized_by', formData.authorized_by || 'Prakarsh Sangave');
    
    // Open Test Report
    const reportUrl = `/LiquidAdmixture/LiquidAdmixtureReport.html?${params.toString()}`;
    window.open(reportUrl, '_blank');
  };

  const handleEditForm = () => {
    setShowPreview(false);
  };

  const handleDownloadPDF = async () => {
    try {
      const success = await generateLiquidAdmixturePDF(formData);
      if (success) {
        alert('PDF downloaded successfully!');
      } else {
        alert('Error generating PDF. Please try again.');
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Error generating PDF. Please try again.');
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    setShowPreview(true);
  };


  // Preview Form Component
  const PreviewForm = () => (
    <div style={{ backgroundColor: '#1C2333', minHeight: '100vh', padding: '10px 0' }}>
      <Container fluid="lg">
        {/* Header with Logos */}
        <Card style={{
          backgroundColor: '#1C2333',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '15px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          marginBottom: '20px'
        }}>
          <Card.Header style={{
            backgroundColor: '#1C2333',
            borderBottom: '2px solid #FFA500',
            padding: '20px'
          }}>
            <Row className="align-items-center">
              <Col xs={12} md={2} className="text-center mb-2 mb-md-0">
                <img 
                  src="/logo.png" 
                  alt="Vitrag Associates Logo" 
                  height="40"
                  style={{ borderRadius: '5px' }}
                />
              </Col>
              <Col xs={12} md={8} className="text-center">
                <h2 style={{ 
                  color: '#ffffff', 
                  fontWeight: '700',
                  margin: '0',
                  fontSize: '1.5rem'
                }}>
                  <FontAwesomeIcon icon={faTint} className="me-2" style={{ color: '#FFA500' }} />
                  LIQUID ADMIXTURE TEST - PREVIEW
                </h2>
              </Col>
              <Col xs={12} md={2} className="text-center mt-2 mt-md-0">
                <img 
                  src="/nabl_logo_final.png" 
                  alt="NABL Logo" 
                  height="40"
                  style={{ borderRadius: '5px' }}
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col className="text-end">
                <Button 
                  onClick={handleEditForm}
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    border: 'none',
                    borderRadius: '25px',
                    padding: '10px 20px',
                    fontWeight: '500',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <FontAwesomeIcon icon={faPencil} className="me-2" />
                  Edit
                </Button>
              </Col>
            </Row>
          </Card.Header>

          <Card.Body style={{ backgroundColor: '#1C2333', padding: '15px' }}>
            {/* General Information Section */}
            <div className="mb-4">
              <div className="table-responsive">
                <Table style={{
                  backgroundColor: '#1C2333',
                  color: '#ffffff',
                  borderCollapse: 'collapse',
                  minWidth: '600px'
                }}>
                  <tbody>
                    <tr>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '25%'
                      }} rowSpan={2}>
                        Customer/Site Name & Address
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }} rowSpan={2}>
                        {customerSearchTerm || formData.customer_id || 'Not selected'}<br />
                        {formData.site_name && <>{formData.site_name}<br /></>}
                        {formData.site_address}
                      </td>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '18%'
                      }}>
                        Date of Receipt
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '25%'
                      }}>
                        {formatDate(formData.date_of_receipt) || '-'}
                      </td>
                    </tr>
                    <tr>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        Date of Testing
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        {formatDate(formData.date_of_testing) || '-'}
                      </td>
                    </tr>
                    <tr>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        URL Number
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        {formData.url_number || '-'}
                      </td>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        Job Code Number
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        {formData.job_code_number || '-'}
                      </td>
                    </tr>
                    <tr>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        Reference Number
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        {formData.reference_number || '-'}
                      </td>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        Sample Test Code
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        {formData.sample_test_code || '-'}
                      </td>
                    </tr>
                    <tr>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        Sample Description
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }} colSpan={3}>
                        {formData.sample_description || '-'}
                      </td>
                    </tr>
                    <tr>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        Environmental Conditions
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        {formData.environmental_conditions || '-'}
                      </td>
                      <th style={{
                        backgroundColor: '#1C2333',
                        color: '#FFD700',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        Test Method
                      </th>
                      <td style={{
                        backgroundColor: '#1C2333',
                        color: '#ffffff',
                        textAlign: 'center',
                        padding: '15px',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        IS 9103:1999
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Test Results Section */}
            <Card style={{
              backgroundColor: '#1C2333',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '15px',
              marginBottom: '20px'
            }}>
              <Card.Header style={{
                backgroundColor: '#1C2333',
                borderBottom: '2px solid #FFA500',
                padding: '15px'
              }}>
                <h5 className="mb-0" style={{ color: '#FFD700', fontWeight: '600' }}>
                  <FontAwesomeIcon icon={faTable} className="me-2" />
                  Test Results
                </h5>
              </Card.Header>
              <Card.Body style={{ backgroundColor: '#1C2333', padding: '15px' }}>
                <div className="table-responsive">
                  <Table style={{
                    backgroundColor: '#1C2333',
                    color: '#ffffff',
                    borderCollapse: 'collapse'
                  }}>
                    <thead>
                      <tr>
                        <th style={{
                          backgroundColor: '#1C2333',
                          color: '#FFD700',
                          textAlign: 'center',
                          padding: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontWeight: '600'
                        }}>Sr. No.</th>
                        <th style={{
                          backgroundColor: '#1C2333',
                          color: '#FFD700',
                          textAlign: 'center',
                          padding: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontWeight: '600'
                        }}>Colour & Texture of Admixture</th>
                        <th style={{
                          backgroundColor: '#1C2333',
                          color: '#FFD700',
                          textAlign: 'center',
                          padding: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontWeight: '600'
                        }}>Volume of Admixture taken (ml)</th>
                        <th style={{
                          backgroundColor: '#1C2333',
                          color: '#FFD700',
                          textAlign: 'center',
                          padding: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontWeight: '600'
                        }}>Temperature of Liquid (°C)</th>
                        <th style={{
                          backgroundColor: '#1C2333',
                          color: '#FFD700',
                          textAlign: 'center',
                          padding: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontWeight: '600'
                        }}>Reading on Hydrometer</th>
                        <th style={{
                          backgroundColor: '#1C2333',
                          color: '#FFD700',
                          textAlign: 'center',
                          padding: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontWeight: '600'
                        }}>Relative Density of Admixture</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3].map((row) => (
                        <tr key={row}>
                          <td style={{
                            backgroundColor: '#1C2333',
                            color: '#ffffff',
                            textAlign: 'center',
                            padding: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>{row}</td>
                          <td style={{
                            backgroundColor: '#1C2333',
                            color: '#ffffff',
                            textAlign: 'center',
                            padding: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>{formData[`colour_texture_${row}`] || '-'}</td>
                          <td style={{
                            backgroundColor: '#1C2333',
                            color: '#ffffff',
                            textAlign: 'center',
                            padding: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>{formData[`volume_ml_${row}`] || '-'}</td>
                          <td style={{
                            backgroundColor: '#1C2333',
                            color: '#ffffff',
                            textAlign: 'center',
                            padding: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>{formData[`temperature_c_${row}`] || '-'}</td>
                          <td style={{
                            backgroundColor: '#1C2333',
                            color: '#ffffff',
                            textAlign: 'center',
                            padding: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>{formData[`hydrometer_reading_${row}`] || '-'}</td>
                          <td style={{
                            backgroundColor: '#1C2333',
                            color: '#ffffff',
                            textAlign: 'center',
                            padding: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}>{formData[`relative_density_${row}`] ? parseFloat(formData[`relative_density_${row}`]).toFixed(2) : '-'}</td>
                        </tr>
                      ))}
                      <tr style={{
                        backgroundColor: '#495057'
                      }}>
                        <td colSpan={5} style={{
                          backgroundColor: '#495057',
                          color: '#ffffff',
                          textAlign: 'center',
                          padding: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontWeight: '700'
                        }}>Average</td>
                        <td style={{
                          backgroundColor: '#495057',
                          color: '#ffffff',
                          textAlign: 'center',
                          padding: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontWeight: '700'
                        }}>{formData.average_relative_density ? parseFloat(formData.average_relative_density).toFixed(2) : '0.00'}</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {/* Verification & Authorization Section */}
            <Card style={{
              backgroundColor: '#1C2333',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '15px',
              marginBottom: '20px'
            }}>
              <Card.Header style={{
                backgroundColor: '#1C2333',
                borderBottom: '2px solid #FFA500',
                padding: '15px'
              }}>
                <h5 className="mb-0" style={{ color: '#FFD700', fontWeight: '600' }}>
                  <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                  Verification & Authorization
                </h5>
              </Card.Header>
              <Card.Body style={{ backgroundColor: '#1C2333', padding: '15px' }}>
                <Row>
                  <Col md={4}>
                    <h6 style={{ color: '#FFD700', fontWeight: '600', marginBottom: '15px' }}>Tested By</h6>
                    <p style={{ color: '#ffffff', marginBottom: '8px' }}>
                      <strong style={{ color: '#FFD700' }}>Name:</strong> {formData.tested_by_name || '-'}
                    </p>
                    <p style={{ color: '#ffffff', marginBottom: '0' }}>
                      <strong style={{ color: '#FFD700' }}>Date:</strong> {formatDate(formData.tested_by_date) || '-'}
                    </p>
                  </Col>
                  <Col md={4}>
                    <h6 style={{ color: '#FFD700', fontWeight: '600', marginBottom: '15px' }}>Checked By</h6>
                    <p style={{ color: '#ffffff', marginBottom: '8px' }}>
                      <strong style={{ color: '#FFD700' }}>Name:</strong> {formData.checked_by_name || '-'}
                    </p>
                    <p style={{ color: '#ffffff', marginBottom: '0' }}>
                      <strong style={{ color: '#FFD700' }}>Date:</strong> {formatDate(formData.checked_by_date) || '-'}
                    </p>
                  </Col>
                  <Col md={4}>
                    <h6 style={{ color: '#FFD700', fontWeight: '600', marginBottom: '15px' }}>Verified By</h6>
                    <p style={{ color: '#ffffff', marginBottom: '8px' }}>
                      <strong style={{ color: '#FFD700' }}>Name:</strong> {formData.verified_by_name || '-'}
                    </p>
                    <p style={{ color: '#ffffff', marginBottom: '0' }}>
                      <strong style={{ color: '#FFD700' }}>Date:</strong> {formatDate(formData.verified_by_date) || '-'}
                    </p>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md={6}>
                    <h6 style={{ color: '#FFD700', fontWeight: '600', marginBottom: '15px' }}>Reviewed By</h6>
                    <p style={{ color: '#ffffff', marginBottom: '0' }}>
                      <strong style={{ color: '#FFD700' }}>Name:</strong> {formData.reviewed_by || '-'}
                    </p>
                  </Col>
                  <Col md={6}>
                    <h6 style={{ color: '#FFD700', fontWeight: '600', marginBottom: '15px' }}>Authorized By</h6>
                    <p style={{ color: '#ffffff', marginBottom: '0' }}>
                      <strong style={{ color: '#FFD700' }}>Name:</strong> {formData.authorized_by || '-'}
                    </p>
                  </Col>
                </Row>
                {formData.remarks && (
                  <Row className="mt-3">
                    <Col>
                      <h6 style={{ color: '#FFD700', fontWeight: '600', marginBottom: '15px' }}>Remarks</h6>
                      <p style={{ color: '#ffffff', marginBottom: '0' }}>{formData.remarks}</p>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            {/* Action Buttons */}
            <Row className="mt-4">
              <Col className="text-center">
                <div className="d-flex flex-column flex-md-row justify-content-center gap-2">
                  <Button
                    variant="info"
                    className="mb-2 mb-md-0"
                    onClick={handleViewObservationSheet}
                    style={{
                      backgroundColor: '#17a2b8',
                      borderColor: '#17a2b8',
                      color: '#ffffff'
                    }}
                  >
                    <FontAwesomeIcon icon={faFlask} className="me-2" />
                    Liquid admixture Observation sheet
                  </Button>
                  
                  <Button
                    variant="warning"
                    onClick={handleViewReport}
                    style={{
                      backgroundColor: '#FFA500',
                      borderColor: '#FFA500',
                      color: '#000000'
                    }}
                  >
                    <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                    Liquid admixture report
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );

  if (showPreview) {
    return <PreviewForm />;
  }

  return (
    <div>
      {/* Header */}
            <div style={{ backgroundColor: '#dc3545', padding: '2rem 0', borderRadius: '15px' }}>
        <Container>
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faTint} className="me-3" style={{ fontSize: '2rem', color: 'white' }} />
                <div>
                  <h1 style={{ color: 'white', fontWeight: '700', margin: 0 }}>
                    Liquid Admixture - Relative Density Test
                  </h1>
                  <p className="mb-0 mt-2" style={{ color: 'white' }}>
                    Test Method: IS 9103:1999
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        <Form onSubmit={handleSubmit}>
          {/* SECTION 1: General Information */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    General Information
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-building me-2" style={{ color: '#FFA500' }}></i>
                          Customer
                        </Form.Label>
                        {customersLoading ? (
                          <div className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" style={{ color: '#FFA500' }} />
                            <span style={{ color: '#e2e8f0' }}>Loading customers...</span>
                          </div>
                        ) : customersError ? (
                          <Alert variant="warning" className="mb-0">
                            {customersError}
                          </Alert>
                        ) : (
                          <div className="customer-dropdown-container" style={{ position: 'relative' }}>
                            <Form.Control
                              type="text"
                              value={customerSearchTerm}
                              onChange={(e) => setCustomerSearchTerm(e.target.value)}
                              onClick={handleCustomerInputClick}
                              placeholder="Search and select customer..."
                              required
                              autoComplete="off"
                              readOnly
                              style={{ 
                                cursor: 'pointer',
                                backgroundColor: '#1C2333',
                                border: '2px solid #6c757d',
                                borderRadius: '8px',
                                color: 'white',
                                padding: '12px 15px',
                                fontSize: '1rem'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#FFA500';
                                e.target.style.boxShadow = '0 0 0 0.25rem rgba(255, 165, 0, 0.25)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#6c757d';
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                            {showCustomerDropdown && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  backgroundColor: '#1C2333',
                                  border: '2px solid #6c757d',
                                  borderTop: 'none',
                                  borderRadius: '0 0 8px 8px',
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                  zIndex: 1000,
                                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
                                }}
                              >
                                {/* Search input inside dropdown */}
                                <div style={{ padding: '15px', borderBottom: '1px solid #6c757d' }}>
                                  <Form.Control
                                    type="text"
                                    placeholder="Search customers..."
                                    value={dropdownSearchTerm}
                                    onChange={handleDropdownSearch}
                                    autoComplete="off"
                                    style={{
                                      backgroundColor: '#242B3C',
                                      border: '2px solid #6c757d',
                                      borderRadius: '8px',
                                      color: 'white',
                                      padding: '10px 12px',
                                      fontSize: '0.9rem'
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.borderColor = '#FFA500';
                                      e.target.style.boxShadow = '0 0 0 0.2rem rgba(255, 165, 0, 0.25)';
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.borderColor = '#6c757d';
                                      e.target.style.boxShadow = 'none';
                                    }}
                                  />
                                </div>
                                
                                {/* Customer list */}
                                {filteredCustomers.length > 0 ? (
                                  filteredCustomers.map((customer) => (
                                    <div
                                      key={customer.id}
                                      onClick={() => handleCustomerSelect(customer)}
                                      style={{
                                        padding: '10px 15px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #6c757d',
                                        color: 'white',
                                        backgroundColor: 'transparent'
                                      }}
                                    >
                                      <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                        {customer.name}
                                      </div>
                                      <div style={{ fontSize: '0.85em', color: '#adb5bd' }}>
                                        {customer.phone && `${customer.phone} • `}
                                        {customer.city && customer.city}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div
                                    style={{
                                      padding: '15px',
                                      color: '#adb5bd',
                                      textAlign: 'center'
                                    }}
                                  >
                                    {dropdownSearchTerm.trim() !== '' 
                                      ? `No customers found matching "${dropdownSearchTerm}"`
                                      : 'No customers available'
                                    }
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                          <i className="fas fa-hashtag me-2" style={{ color: '#FFA500' }}></i>
                          URL Number
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="url_number"
                          value={formData.url_number}
                          onChange={handleInputChange}
                          placeholder="Enter URL number for reference"
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                          <i className="fas fa-barcode me-2" style={{ color: '#FFA500' }}></i>
                          Job Code Number
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="job_code_number"
                          value={formData.job_code_number}
                          onChange={handleInputChange}
                          placeholder="Enter job code number for reference"
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                          <i className="fas fa-file-alt me-2" style={{ color: '#FFA500' }}></i>
                          Reference Number
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="reference_number"
                          value={formData.reference_number}
                          onChange={handleInputChange}
                          placeholder="Enter reference number for tracking"
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-flask me-2" style={{ color: '#FFA500' }}></i>
                          Sample Description
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="sample_description"
                          value={formData.sample_description}
                          onChange={handleInputChange}
                          placeholder="Describe the liquid admixture sample..."
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-calendar-alt me-2" style={{ color: '#FFA500' }}></i>
                          Date of Receipt
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="date_of_receipt"
                          value={formData.date_of_receipt}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-vial me-2" style={{ color: '#FFA500' }}></i>
                          Sample Test Code Number
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="sample_test_code"
                          value={formData.sample_test_code}
                          onChange={handleInputChange}
                          placeholder="e.g., SCN-2024001"
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-calendar-check me-2" style={{ color: '#FFA500' }}></i>
                          Date of Testing
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="date_of_testing"
                          value={formData.date_of_testing}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                          <i className="fas fa-thermometer-half me-2" style={{ color: '#FFA500' }}></i>
                          Environmental Conditions
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="environmental_conditions"
                          value={formData.environmental_conditions}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                          <i className="fas fa-map-marker-alt me-2" style={{ color: '#FFA500' }}></i>
                          Site Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="site_name"
                          value={formData.site_name}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                          <i className="fas fa-map-marked-alt me-2" style={{ color: '#FFA500' }}></i>
                          Site Address
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="site_address"
                          value={formData.site_address}
                          onChange={handleInputChange}
                          placeholder="Enter site address"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 2: Test Results */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faFlask} className="me-2" />
                    Test Results
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <table className="table transparent-table" style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%', border: '2px solid #6c757d', borderRadius: '8px', backgroundColor: '#1C2333' }}>
                      <thead className="table-dark">
                        <tr>
                          <th style={{ width: '50px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Sr. No.</th>
                          <th style={{ width: '200px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Colour & Texture of Admixture</th>
                          <th style={{ width: '150px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Volume of Admixture taken (ml)</th>
                          <th style={{ width: '150px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Temperature of Liquid (°C)</th>
                          <th style={{ width: '150px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Reading on Hydrometer</th>
                          <th style={{ width: '200px', padding: '10px', border: '1px solid #6c757d', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.95rem', backgroundColor: '#6c757d', color: 'white', fontWeight: '700' }}>Relative Density of Admixture</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[1, 2, 3].map((row) => (
                          <tr key={row} style={{ backgroundColor: '#1C2333' }}>
                            <td style={{ padding: '8px', border: '1px solid #6c757d', textAlign: 'center', fontWeight: 'bold', backgroundColor: '#1C2333', color: 'white' }}>{row}</td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="text"
                                name={`colour_texture_${row}`}
                                value={formData[`colour_texture_${row}`]}
                                onChange={handleInputChange}
                                style={{ fontSize: '0.85rem', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.01"
                                name={`volume_ml_${row}`}
                                value={formData[`volume_ml_${row}`]}
                                onChange={handleInputChange}
                                style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                name={`temperature_c_${row}`}
                                value={formData[`temperature_c_${row}`]}
                                onChange={handleInputChange}
                                style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.0001"
                                name={`hydrometer_reading_${row}`}
                                value={formData[`hydrometer_reading_${row}`]}
                                onChange={handleInputChange}
                                style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #6c757d' }}>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.0001"
                                name={`relative_density_${row}`}
                                value={formData[`relative_density_${row}`]}
                                onChange={(e) => {
                                  handleInputChange(e);
                                  setTimeout(() => calculateAverage(), 0);
                                }}
                                style={{ fontSize: '0.85rem', textAlign: 'center', backgroundColor: '#242B3C', color: 'white', border: '1px solid #6c757d' }}
                              />
                            </td>
                          </tr>
                        ))}
                        {/* Average Row */}
                        <tr className="table-info" style={{ backgroundColor: '#495057' }}>
                          <td colSpan="5" className="text-center fw-bold" style={{ padding: '10px', border: '1px solid #6c757d', fontSize: '1rem', backgroundColor: '#495057', color: 'white', fontWeight: '700' }}>Average</td>
                          <td className="text-center fw-bold" style={{ padding: '8px', border: '1px solid #6c757d', backgroundColor: '#495057' }}>
                            <Form.Control
                              size="sm"
                              type="number"
                              step="0.0001"
                              name="average_relative_density"
                              value={formData.average_relative_density}
                              readOnly
                              className="average-input-transparent"
                              style={{ 
                                fontSize: '0.9rem', 
                                textAlign: 'center',
                                backgroundColor: '#495057',
                                border: '1px solid #6c757d',
                                borderRadius: '6px',
                                color: '#ffffff',
                                fontWeight: '700',
                                cursor: 'not-allowed'
                              }}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 3: Verification */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Verification
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Row>
                    <Col md={4}>
                      <h6>Tested By</h6>
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-user me-2" style={{ color: '#FFA500' }}></i>
                          Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="tested_by_name"
                          value={formData.tested_by_name}
                          onChange={handleInputChange}
                          placeholder="Enter tester name"
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-calendar me-2" style={{ color: '#FFA500' }}></i>
                          Date
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="tested_by_date"
                          value={formData.tested_by_date}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Checked By</h6>
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-user-check me-2" style={{ color: '#FFA500' }}></i>
                          Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="checked_by_name"
                          value={formData.checked_by_name}
                          onChange={handleInputChange}
                          placeholder="Enter checker name"
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-calendar me-2" style={{ color: '#FFA500' }}></i>
                          Date
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="checked_by_date"
                          value={formData.checked_by_date}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6>Verified By</h6>
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center">
                          <i className="fas fa-user-shield me-2" style={{ color: '#FFA500' }}></i>
                          Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="verified_by_name"
                          value={formData.verified_by_name}
                          readOnly
                          style={{ backgroundColor: '#495057', borderColor: '#6c757d', cursor: 'not-allowed' }}
                        />
                      </Form.Group>
                      <Form.Group className="mb-4">
                        <Form.Label className="required-field d-flex align-items-center">
                          <i className="fas fa-calendar me-2" style={{ color: '#FFA500' }}></i>
                          Date
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="verified_by_date"
                          value={formData.verified_by_date}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 4: Remarks */}
          <Row className="mb-4">
            <Col>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faComment} className="me-2" />
                    Remarks
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <i className="fas fa-comment-alt me-2" style={{ color: '#FFA500' }}></i>
                      Additional Remarks
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      placeholder="Enter any additional remarks or observations..."
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* SECTION 5: Review & Authorization */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Reviewed By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Label className="required-field d-flex align-items-center">
                      <i className="fas fa-user-tie me-2" style={{ color: '#FFA500' }}></i>
                      Select Reviewer
                    </Form.Label>
                    {loadingReviewers ? (
                      <div className="d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" style={{ color: '#FFA500' }} />
                        <span style={{ color: '#e2e8f0' }}>Loading reviewers...</span>
                      </div>
                    ) : (
                      <Form.Select 
                        name="reviewed_by" 
                        value={formData.reviewed_by}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">-- Select a reviewer --</option>
                        {reviewers.map((reviewer) => (
                          <option 
                            key={reviewer.id} 
                            value={`${reviewer.name} - ${reviewer.designation}`}
                          >
                            {reviewer.name} - {reviewer.designation}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="card-vitrag shadow-sm">
                <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
                  <h5 className="mb-0" style={{ color: 'var(--vitrag-gold)' }}>
                    <FontAwesomeIcon icon={faUserCheck} className="me-2" />
                    Authorized By
                  </h5>
                </Card.Header>
                <Card.Body className="transparent-input-section">
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <i className="fas fa-user-crown me-2" style={{ color: '#FFA500' }}></i>
                      Authorized By
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="authorized_by"
                      value={formData.authorized_by}
                      readOnly
                      style={{ backgroundColor: '#495057', borderColor: '#6c757d', cursor: 'not-allowed' }}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row>
            <Col className="text-center">
              <Button 
                onClick={handleSaveTestData}
                variant="primary" 
                size="lg"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save Test Data
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>

      {/* Loading Overlay for Data Saving */}
      {isSaving && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '10px',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <Spinner 
              animation="border" 
              variant="primary" 
              role="status" 
              style={{ width: '3rem', height: '3rem' }}
            >
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <div className="mt-3">
              <h5 className="text-dark mb-2">Saving Test Data...</h5>
              <p className="text-muted mb-0">Please wait while we save your data to the database</p>
            </div>
          </div>
        </div>
      )}

      {/* Preview Section - Now handled by PreviewForm component above */}
    </div>
  );
};

export default LiquidAdmixtureForm;
